import { useMemo } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { MINUTE } from 'time-constants';

import { apiConfig } from '@constants';

import { TimeUtils } from '../../helpers/TimeUtils';

const LIGHTNING_REFETCH_INTERVAL = 5 * MINUTE;

const getLightningURL = startDateTime =>
  `${apiConfig.TWC_API_BASE}${apiConfig.TWC_API_US_LIGHTNING}?startDateTime=${startDateTime}&format=json&units=e&apiKey=${apiConfig.TWC_API_MAP_TILE_KEY}`;

const getLightning = async startDateTime => {
  const { data } = await axios.get(getLightningURL(startDateTime));

  if (data?.lightning) {
    const { latitude: latitudes = [], longitude: longitudes = [] } = data.lightning;

    if (latitudes.length === longitudes.length) {
      return latitudes.map((latitude, index) => ({
        latitude,
        longitude: longitudes[index],
      }));
    }
  }
  return [];
};

// This function will calculate and return the starting time for the last three 5-minute
// time blocks for which to fetch lightning data.
const fiveMinuteDateStrings = () => {
  // Give the API 30 seconds after the last 5 minute time period to offer results.
  const referenceDate = TimeUtils.dateByAddingTimeInterval(new Date(), -30);
  // Get just the minutes value of the reference date.
  const referenceDateMinutes = TimeUtils.getMinuteTime(referenceDate);
  // We need to go back to the nearest 5 minute period, so calculate how
  // many minutes we are past the end of the last 5 minute interval.
  const minutesBeyondLast5MinuteInterval = referenceDateMinutes % 5;

  return [
    TimeUtils.utcDateString(
      TimeUtils.dateByAddingTimeInterval(referenceDate, -(minutesBeyondLast5MinuteInterval + 15) * 60),
      'YYYYMMDDkkmm',
    ),
    TimeUtils.utcDateString(
      TimeUtils.dateByAddingTimeInterval(referenceDate, -(minutesBeyondLast5MinuteInterval + 10) * 60),
      'YYYYMMDDkkmm',
    ),
    TimeUtils.utcDateString(
      TimeUtils.dateByAddingTimeInterval(referenceDate, -(minutesBeyondLast5MinuteInterval + 5) * 60),
      'YYYYMMDDkkmm',
    ),
  ];
};

export const useGetLightning = () => {
  const dateStrings = fiveMinuteDateStrings();

  const { data: minute5Strikes } = useQuery(`getLightning-${dateStrings[2]}`, () => getLightning(dateStrings[2]), {
    refetchInterval: LIGHTNING_REFETCH_INTERVAL,
    staleTime: LIGHTNING_REFETCH_INTERVAL,
  });
  const { data: minute10Strikes } = useQuery(`getLightning-${dateStrings[1]}`, () => getLightning(dateStrings[1]), {
    refetchInterval: LIGHTNING_REFETCH_INTERVAL,
    staleTime: LIGHTNING_REFETCH_INTERVAL,
  });
  const { data: minute15Strikes } = useQuery(`getLightning-${dateStrings[0]}`, () => getLightning(dateStrings[0]), {
    refetchInterval: LIGHTNING_REFETCH_INTERVAL,
    staleTime: LIGHTNING_REFETCH_INTERVAL,
  });

  return useMemo(
    () => [minute5Strikes, minute10Strikes, minute15Strikes].filter(Boolean).flat(),
    [minute5Strikes, minute10Strikes, minute15Strikes],
  );
};
